/* eslint-disable max-len */
import React from 'react';

import Link from '../components/Link';
import {getBrandName} from '../components/Brand';

const P2PGeneral = [{
  question: 'How does it work?',
  answer: (
    <>
      {`Every day at pre-scheduled times, ${getBrandName()} facilitates peer-to-peer mock interview sessions for tech roles.`}
      <br />
      <br />
      Interviews are live one-on-one video calls hosted on our website, with a built-in shared code editor for technical interviews.
      <br />
      <br />
      {`${getBrandName()} automatically matches you with peers based on your availability, practice needs, and interview objectives.`}
      <br />
      <br />
      We manage the interview scheduling and supply potential questions. For technical questions, we&apos;ll also share hints, test cases, and the solution.
      <br />
      <br />
      Each practice session is reciprocal: everyone takes turns as both the interviewee and interviewer.
      <br />
      <br />
      Interviewing others is beneficial to you too! Being the interviewer provides insights into best interview practices.
    </>
  ),
}, {
  question: 'What kind of interviews can I practice?',
  answer: (
    <span>
      We support these roles and interview types:
      <br />
      <br />
      <ul className="list-inside list-disc">
        <li>
          <span className="font-semibold">Product Management:</span>
          {' Product sense, estimation, product design, and analytical questions.'}
        </li>
        <li>
          <span className="font-semibold">Data Structures & Algorithms:</span>
          {' Coding questions that test fundamental CS concepts.'}
        </li>
        <li>
          <span className="font-semibold">System Design:</span>
          {' Design scalable systems and weigh trade-offs.'}
        </li>
        <li>
          <span className="font-semibold">Behavioral:</span>
          {' Work experience and culture-fit questions for all tech roles.'}
        </li>
        <li>
          <span className="font-semibold">Finance:</span>
          {' Valuation, accounting, M&A, and markets technical questions.'}
        </li>
        <li>
          <span className="font-semibold">SQL:</span>
          {' (Beta)'}
        </li>
        <li>
          <span className="font-semibold">Data Science & ML:</span>
          {' (Beta)'}
        </li>
        <li>
          <span className="font-semibold">Frontend:</span>
          {' (Beta)'}
        </li>
      </ul>
    </span>
  ),
}, {
  question: 'How do I join an interview?',
  answer: (
    <span>
      You can participate in an interview by joining at the last minute or scheduling it in advance.
      <br />
      <br />
      To join last minute, return to this page ten minutes before the next scheduled session.
      <br />
      <br />
      To schedule a peer-to-peer mock interview for later:
      <br />
      <br />
      <ol className="list-inside list-decimal">
        <li>Click on &quot;Schedule practice session&quot; above.</li>
        <li>Select an interview type: product management, behavioral, data structures & algorithms, or system design.</li>
        <li>Choose a date, time, and difficulty level that first your schedule and prep needs.</li>
        <li>Add the session to your calendar and receive automatic email reminders.</li>
        <li>In the ten minutes before your start time, return to this page to begin your interview.</li>
      </ol>
    </span>
  ),
}, {
  question: 'When do interviews happen?',
  answer: (
    <span>
      This is our daily interview schedule. This is the time each mock interview session starts. Return to this page ten minutes before your scheduled time to join.
      <br />
      <br />
      <ul className="list-inside list-disc">
        <li>
          <span className="font-semibold">Product Management:</span>
          {' 2 AM, 8 AM, 12 PM, and 6 PM Pacific Time.'}
        </li>
        <li>
          <span className="font-semibold">Data Structures & Algorithms:</span>
          {' Every two hours between 8 AM and midnight, local time.'}
        </li>
        <li>
          <span className="font-semibold">System Design:</span>
          {' 2 AM, 8 AM, 12 PM, and 6 PM Pacific Time.'}
        </li>
        <li>
          <span className="font-semibold">Behavioral:</span>
          {' 2 AM, 8 AM, 12 PM, and 6 PM Pacific Time.'}
        </li>
        <li>
          <span className="font-semibold">Finance:</span>
          {' 8 AM Pacific Time on Sundays and Wednesdays.'}
        </li>
        <li>
          <span className="font-semibold">SQL:</span>
          {' 8 AM and 6 PM Pacific Time.'}
        </li>
        <li>
          <span className="font-semibold">Data Science & ML:</span>
          {' 6 AM and 4 PM Pacific Time.'}
        </li>
        <li>
          <span className="font-semibold">Frontend:</span>
          {' 6 AM and 4 PM Pacific Time.'}
        </li>
      </ul>
    </span>
  ),
}, {
  question: 'Will I be reminded about my interview?',
  answer: 'Yes, we will send you an email confirmation and reminders. You can add the session to your Google Calendar from the confirmation email or from the dashboard.',
}, {
  question: 'Is it free?',
  answer: (
    <span>
      {`${getBrandName()}'s mock interview practice tool is free to use. Free users are allotted credits to practice every month. Members enjoy`}
      {' '}
      <Link href="/upgrade" className="text-indigo">unlimited access</Link>
      .
    </span>
  ),
}, {
  question: `I used Pramp before. Do I need an ${getBrandName()} account? `,
  answer: (
    <span>
      {`Pramp is now ${getBrandName({fka: true})}.`}
      <br />
      <br />
      {`You can schedule the same types of mock interviews on ${getBrandName()} as you could on Pramp, however you'll need an ${getBrandName()} account.`}
      <br />
      <br />
      If you have a Pramp account,
      {' '}
      <Link href="https://www.pramp.com/exponent_auth/practice" className="text-indigo">{`click here to link your ${getBrandName()} account`}</Link>
      .
    </span>
  ),
}, {
  question: 'Where can I share feedback?',
  answer: `We'd love to hear your suggestions on how we can improve ${getBrandName()} Practice. Reach out to us with comments, concerns, or ideas at practice@aced.io.`,
}, {
  ai: true,
  question: 'How does the AI interview feedback work?',
  answer: (
    <span>
      {'For every peer mock interview type, you\'ll receive an automatic transcript of your interview, and AI will grade your interview according to realistic hiring rubrics that have been developed with best practices in mind. Your interview feedback will include a score for each attribute and a recommendation on how to improve your scores.'}
      <br />
      <br />
      You&apos;ll be able to view this feedback alongside your peer feedback soon after your session is complete, and we&apos;ll send an email notification when it is ready.
      <br />
      <br />
      {`Note: This feature is in early access and is currently only available for ${getBrandName()} members. To unlock feedback and transcripts, you'll need to`}
      {' '}
      <Link href="/upgrade" className="text-indigo">upgrade</Link>
      {' '}
      to a paid membership.
      <br />
      <br />
      You have the option to opt-out from AI feedback at any time by updating your
      {' '}
      <Link href="/account#aced-practice" className="text-indigo">account settings</Link>
      .
    </span>
  ),
}];

const P2PMatching = [{
  question: 'Can I choose my partner?',
  answer: (
    <span>
      In most cases, we automatically match you with a suitable partner based on experience level and interview history.
      <br />
      <br />
      If you get along well, you can use the
      {' '}
      <span className="font-semibold">Intro</span>
      {' '}
      feature on your dashboard to exchange information with a previous partner. Stay in touch and continue practicing together!
      <br />
      <br />
      If you want to practice with someone you know, you can use the
      {' '}
      <span className="font-semibold">Practice with a Friend</span>
      {' '}
      feature.
    </span>
  ),
}, {
  question: 'How does the matching process work?',
  answer: 'Our matching algorithm considers several factors to pair you with a suitable peer for practice, including your schedule and availability, current role, experience level, and interviewing history.',
}, {
  question: 'What if I can\'t attend a scheduled interview?',
  answer: (
    <span>
      Life happens!
      <br />
      <br />
      If you&apos;re unable to attend your interview, please log in to your account and either cancel or reschedule it prior to the session start time.
      <br />
      <br />
      You will have your interview credit returned to your account.
    </span>
  ),
}];

const P2PInterviewing = [{
  question: 'What does a typical interview involve?',
  answer: (
    <span>
      Each interview involves you and your partner interviewing each other for approximately 30 minutes each, making the entire session about an hour long.
      <br />
      <br />
      You will be connected via a video call. For Data Structures & Algorithms, SQL, and Frontend interviews, you will solve your question using a collaborative code editor. For all other interview types, suggested questions will be offered before and during the session.
      <br />
      <br />
      Interviewer and interviewee roles are randomly assigned at the start of the interview, and you switch halfway through.
      <br />
      <br />
      While interviews typically last an hour, you will have access to the video call for up to two hours. The interview will automatically end after this period.
    </span>
  ),
}, {
  question: 'What types of questions will I get?',
  answer: (
    <span>
      It depends on the interview type you select.
      <br />
      <br />
      For Data Structures & Algorithms, SQL, and Frontend interviews, your peer will be preassigned a coding question to ask you.
      <br />
      <br />
      For all other interview types, suggested questions will be offered before and during the session. These questions come from our database, which includes real-world questions seen by candidates in recent interviews.
    </span>
  ),
}, {
  question: 'What questions should I ask?',
  answer: (
    <span>
      It depends on the interview type you select.
      <br />
      <br />
      {`For Data Structures & Algorithms, SQL, and Frontend interviews, you will be preassigned a coding question to ask your partner. Usually, you are assigned a question you've already seen on ${getBrandName()} Practice, so that you are already familiar with the problem. We strongly suggest practicing the question before your interview.`}
      <br />
      <br />
      For all other interview types, suggested questions will be offered before and during the session. These questions come from our database, which includes real-world questions seen by candidates in recent interviews. You are also welcome to bring your own question or ask your partner what they would like to practice!
    </span>
  ),
}, {
  question: 'Are interviews anonymous?',
  answer: (
    <span>
      We prioritize your privacy.
      <br />
      <br />
      Only your public display name will be shared with your partner. (Your public display name can be updated on your
      {' '}
      <Link href="/account" className="text-indigo" target="_blank">Account</Link>
      {' '}
      page.)
      <br />
      <br />
      During and after the interview, you can choose to exchange information with your practice partner. Doing so is a great way to stay in touch and continue practicing together!
      <br />
      <br />
      We recommend leaving video on, but you also have the option to conduct the interview with audio only.
    </span>
  ),
}, {
  question: 'How do I join an upcoming interview?',
  answer: (
    <span>
      Return to this page up to ten minutes before your scheduled start time. When the waiting room is open, an option to join will appear.
      <br />
      <br />
      We&apos;ll also send you an email reminders ten minutes before your interview.
    </span>
  ),
}, {
  question: 'Can I run code during the interview?',
  answer: (
    <span>
      The Data Structures & Algorithms, SQL, and Frontend mock interview types include a built-in collaborative code editor.
      <br />
      <br />
      Data Structures & Algorithms interviews support popular programming languages like Python, JavaScript, Java, Go, C++, C, C#, Ruby, and Swift. SQL interviews support PostgreSQL and SQLite. Frontend interviews are in JavaScript.
    </span>
  ),
}];

const P2PTroubleshooting = [{
  question: 'Why isn\'t my video or audio working?',
  answer: (
    <span>
      Our video chat uses a peer-to-peer video connection, which directly connects peers.
      <br />
      <br />
      This means most issues typically result from browser support, permissions, or an unstable internet connection.
      <br />
      <br />
      {`Ensure your browser has permission to access your camera and microphone. Ensure your browser has given ${getBrandName()} permission to access your camera and microphone. Confirm that your browser supports WebRTC.`}
      <br />
      <br />
      <span className="text-lg font-semibold">Troubleshooting Tips</span>
      <br />
      <br />
      <ul className="list-inside list-disc">
        <li>
          Try refreshing the page, but avoid clicking &quot;End Session.&quot;
        </li>
        <li>
          Ensure your browser has permission to access your camera and microphone.
        </li>
        <li>
          {`Ensure your browser has given the ${getBrandName()} website permission to access your camera and microphone.`}
        </li>
        <li>
          <Link href="https://webcasts.com/webrtc/" className="text-indigo" target="_blank">Run a test</Link>
          {' '}
          to check your browser&apos;s WebRTC support.
        </li>
        <li>
          If you have multiple cameras or microphones installed, your browser or operating system may be using the wrong one. Check and adjust if needed.
        </li>
        <li>
          Close all browser windows and restart your browser application.
        </li>
        <li>
          If another app or browser is using the camera, quit the application or restart your computer.
        </li>
        <li>
          If your connection is slow, consider using a wired connection instead of WiFi.
        </li>
      </ul>
      <br />
      <br />
      If the above suggestions don&apos;t resolve your issue, please email us at practice@aced.io
    </span>
  ),
}, {
  question: 'Can I turn off my video?',
  answer: 'Yes, you can disable your video during calls. However, we recommend leaving it on to receive feedback on your soft skills!',
}, {
  question: 'Which browsers are supported?',
  answer: 'We require recent versions of Chrome, Safari, Firefox or Edge. Other browsers may work, but we don\'t officially support them.',
}, {
  question: 'Can I use a mobile device?',
  answer: 'We don\'t support mock interviewing on mobile devices. Many features in our mock interview tool are designed for desktop use.',
}];

const AIGeneral = [{
  question: 'How does it work?',
  answer: (
    <span>
      You can practice real interview questions over audio chat and receive actionable AI feedback on your responses. The system transcribes your answers and evaluates them using realistic hiring rubrics.
      <br />
      <br />
      Each session lasts 10-20 minutes and focuses on common interview questions for the interview type you choose. Questions are automatically assigned at the start of the session, and the transcript is used to analyze your performance.
      <br />
      <br />
      You&apos;ll receive a scored rubric based on criteria like communication, problem-solving, and structured thinking.
      <br />
      <br />
      You can access your transcripts and rubrics after the session ends from your dashboard.
    </span>
  ),
}, {
  question: 'What types of interviews can I practice?',
  answer: 'You can practice product management, behavioral, system design, data structures & algorithms, SQL, and investment banking interviews. The AI evaluates your responses using hiring criteria and scoring attributes modeled after those used at top tech companies.',
}, {
  question: 'How do I join an interview?',
  answer: (
    <span>
      You can join an on-demand AI interview session anytime that fits your schedule. You&apos;ll automatically receive a common interview question and can begin practicing immediately.
      <br />
      <br />
      To start, go to the
      {' '}
      <Link href="/practice" className="text-indigo">{`${getBrandName()} Practice`}</Link>
      {' '}
      dashboard and select “Start an AI interview.” You must be logged in to access this feature.
    </span>
  ),
}, {
  question: 'Is it free?',
  answer: `AI-generated feedback and transcripts are available for paid ${getBrandName()} members only. A paid membership is required to unlock these features.`,
}, {
  question: 'Where can I share feedback?',
  answer: (
    <span>
      {`We'd love to hear your suggestions for improving ${getBrandName()} Practice.`}
      <br />
      <br />
      Reach out with comments, concerns, or ideas at practice@aced.io.
    </span>
  ),
}, {
  question: 'How does AI interview feedback work?',
  answer: (
    <span>
      Our AI evaluates your interview using industry-standard rubrics. You&apos;ll receive a clear score across multiple skills and personalized tips to help you improve.
      <br />
      <br />
      Feedback appears immediately after your session ends.
      <br />
      <br />
      {`Note: This feature is currently in beta and available only to ${getBrandName()} members. To access transcripts and feedback, upgrade to a paid membership.`}
      <br />
      <br />
      Don&apos;t want AI feedback? You can disable it anytime in your
      {' '}
      <Link href="/account#aced-practice" className="text-indigo">account settings</Link>
      .
    </span>
  ),
}];

const AIInterviewing = [
  {
    question: 'What does a typical interview involve?',
    answer: (
      <span>
        Each AI interview lasts 10-20 minutes and connects you via audio to an AI mock interviewer.
        <br />
        <br />
        You&apos;ll be prompted to begin the session and will receive a randomly assigned question. You can also shuffle to get a new one during the call.
        <br />
        <br />
        As you speak, your audio is transcribed in real time. Once the session ends, your transcript is automatically evaluated using hiring rubrics modeled after real-world interviews at leading tech companies.
      </span>
    ),
  },
  {
    question: 'What types of questions will I get?',
    answer: (
      <span>
        It depends on the interview type you choose.
        <br />
        <br />
        <ul className="list-inside list-disc">
          <li>
            <b>Product management</b>
            {' '}
            interviews may include product strategy, product sense, execution, or analytical questions.
          </li>
          <li>
            <b>Behavioral</b>
            {' '}
            interviews may focus on leadership, project and people management, cross-functional collaboration, achievements, challenges, and growth.
          </li>
          <li>
            <b>System design</b>
            {' '}
            interviews ask you to design and scale technical architectures.
          </li>
          <li>
            <b>Data structures & algorithms</b>
            {' and '}
            <b>SQL</b>
            {' '}
            interviews are coding problems you solve in a live code editor.
          </li>
          <li>
            <b>Investment banking</b>
            {' '}
            interviews cover fit questions followed by technical finance questions.
          </li>
        </ul>
      </span>
    ),
  },
  {
    question: 'Can I run code during my AI interviews?',
    answer: 'Yes! Data structures & algorithms and SQL AI interviews include a code editor where you can write and run your code while talking through your solution with the AI interviewer.',
  },
  {
    question: 'Are interviews anonymous?',
    answer: (
      <span>
        Yes! Your AI interviewer is designed to be anonymous and won&apos;t share any personal information.
        <br />
        <br />
        You can also choose to keep your camera off during the interview.
      </span>
    ),
  },
];

const AITroubleshooting = [{
  question: 'Why isn\'t my audio working?',
  answer: (
    <span>
      Most issues typically result from browser support, permissions, or an unstable internet connection.
      <br />
      <br />
      Ensure your browser has permission to access your microphone.
      <br />
      <br />
      <span className="text-lg font-semibold">Troubleshooting Tips</span>
      <br />
      <br />
      <ul className="list-inside list-disc">
        <li>
          Try refreshing the page, but avoid clicking &quot;End Session.&quot;
        </li>
        <li>
          If you have multiple cameras or microphones installed, your browser or
          operating system may be using the wrong one. Check and adjust if needed.
        </li>
        <li>
          If another app or browser is using the microphone, quit the application or restart your computer.
        </li>
        <li>
          If your connection is slow, consider using a wired connection instead of WiFi.
        </li>
      </ul>
      <br />
      <br />
      If the above suggestions don&apos;t resolve your issue, please email us at practice@aced.io
    </span>
  ),
}, {
  question: 'Can I turn off my video?',
  answer: 'Video is disabled by default during AI interviews.',
}, {
  question: 'Which browsers are supported?',
  answer: 'We require recent versions of Chrome, Safari, Firefox or Edge. Other browsers may work, but we don\'t officially support them.',
}, {
  question: 'Can I use a mobile device?',
  answer: 'Yes, AI mock interviews are supported on mobile devices. However, peer mock interviews are only available on desktop.',
}];

const DuetFAQs = {
  P2PGeneral,
  P2PMatching,
  P2PInterviewing,
  P2PTroubleshooting,
  AIGeneral,
  AIInterviewing,
  AITroubleshooting,
};

export default DuetFAQs;
